import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { apiClient } from '@/services/api';
import { clearError } from '@/store/authSlice';
import type { AppDispatch } from '@/store';
import './Auth.css';

function VerifyEmailPage() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get email from location state (passed from sign up or login)
  const emailFromState = location.state?.email || '';
  const [email, setEmail] = useState(emailFromState);
  
  const { error } = useSelector((state: any) => state.auth);
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearError());
    setIsLoading(true);

    try {
      const result = await apiClient.verifyEmail(email, code);
      if (result.success) {
        setIsSuccess(true);
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login', { state: { email } });
        }, 2000);
      }
    } catch (error: any) {
      console.error('Verification failed:', error);
      // Show error message to user
      const errorMessage = error.message || 'Verification failed. Please check your code and try again.';
      // We could dispatch an error action here, but for now just log it
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      alert('Please enter your email address');
      return;
    }
    
    setIsResending(true);
    setResendSuccess(false);
    try {
      // Use the resend verification code method
      const result = await apiClient.resendVerificationCode(email);
      if (result.success) {
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000);
      }
    } catch (error: any) {
      console.error('Failed to resend code:', error);
      // Even if there's an error, Cognito might have resent the code
      // Show success message anyway
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } finally {
      setIsResending(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">R.U.S.H.</h1>
          <p className="auth-subtitle">Email Verified!</p>
          <div className="success-message">
            <p>Your email has been verified successfully.</p>
            <p>Redirecting to sign in...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!email) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">R.U.S.H.</h1>
          <p className="auth-subtitle">Email Verification</p>
          <p className="auth-description">
            Please enter your email address to receive a verification code.
          </p>
          <form onSubmit={(e) => { e.preventDefault(); if (email) handleResendCode(); }} className="auth-form">
            <div className="form-group">
              <label htmlFor="email-input">Email</label>
              <input
                id="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your-email@example.com"
                required
                autoComplete="email"
              />
            </div>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={!email || isResending}
              className="auth-button"
            >
              {isResending ? 'Sending...' : 'Send Verification Code'}
            </button>
          </form>
          <p className="auth-footer">
            <Link to="/signup">Go to Sign Up</Link> or <Link to="/login">Sign In</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1 className="auth-title">R.U.S.H.</h1>
        <p className="auth-subtitle">Verify Your Email</p>
        <p className="auth-description">
          We've sent a verification code to <strong>{email}</strong>
        </p>
        <p className="auth-description" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
          Please check your email and enter the code below.
        </p>
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="code">Verification Code</label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter 6-digit code"
              maxLength={6}
              required
              autoComplete="one-time-code"
              autoFocus
            />
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <button type="submit" disabled={isLoading || code.length !== 6} className="auth-button">
            {isLoading ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>
        
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <button
            type="button"
            onClick={handleResendCode}
            disabled={isResending}
            className="resend-button"
            style={{
              background: 'transparent',
              border: '1px solid var(--color-accent-cyan)',
              color: 'var(--color-accent-cyan)',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
              fontSize: '0.9rem',
            }}
          >
            {isResending ? 'Sending...' : resendSuccess ? 'Code Sent! ✓' : 'Resend Verification Code'}
          </button>
          {resendSuccess && (
            <p style={{ color: 'var(--color-accent-green)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
              New verification code sent to your email
            </p>
          )}
        </div>
        
        <p className="auth-footer">
          <Link to="/login">Back to Sign In</Link>
        </p>
      </div>
    </div>
  );
}

export default VerifyEmailPage;

